/**
 * How every Higgins SPA talks to its own API (#312) — previously a copy of
 * this in each app's src/lib/api.ts, already drifting apart.
 *
 * - The bearer goes in X-App-Authorization AND Authorization: SWA's proxy
 *   replaces Authorization before it reaches managed functions, so the APIs
 *   (@higgins/api-auth) read X-App-Authorization first; Authorization stays
 *   for local `func start` and linked Function Apps.
 * - A failure becomes an ApiError carrying the API's message, its
 *   machine-readable `code` (STALE, OVERLAP …) and the whole body.
 * - A 204 resolves to undefined. Any other empty or broken success body means
 *   the SWA gateway dropped or truncated the reply after the work was accepted
 *   (it cuts requests off around 45 s), so say that honestly rather than
 *   "unexpected end of JSON input".
 */

import type { FeedbackReport } from './feedback-queue';

/**
 * The last few things that went wrong, kept in memory for the Report a problem
 * button (#660, FEEDBACK-PLAN.md §11.2): the messages the user was shown and
 * the server calls that failed. A report carries them in its Diagnostics, so
 * triage sees what happened just before the user pressed Send.
 *
 * Collected in the shared code only — createApiClient notes every failed call,
 * @higgins/ui's error display notes what it showed — so every app gets them
 * with no code of its own. Memory only: gone when the page reloads. Never a
 * token, a request body or a query string.
 */

/** How many of each kind are kept; the oldest goes first. */
export const RECENT_ERRORS_MAX = 10;
/** Longest message kept; a server's reason past this is cut. */
export const RECENT_ERROR_MESSAGE_MAX = 300;
const ROUTE_MAX = 200;
const CODE_MAX = 50;

/** A message the user saw (an error toast, the error page). */
export interface ShownError {
  message: string;
  /** ISO time. */
  at: string;
}

/** A call to the app's API that failed. Status 0 is no answer at all (no signal). */
export interface FailedCall {
  /** The path called, with no query string. */
  route: string;
  method: string;
  status: number;
  code?: string;
  /** The server's reason, as the user would have seen it. */
  message: string;
  /** ISO time. */
  at: string;
}

export interface RecentErrors {
  shown: ShownError[];
  failedCalls: FailedCall[];
}

let shown: ShownError[] = [];
let failedCalls: FailedCall[] = [];

const cut = (value: string, max: number) => (value.length > max ? `${value.slice(0, max - 1)}…` : value);

function keepLast<T>(list: T[], item: T): T[] {
  return [...list, item].slice(-RECENT_ERRORS_MAX);
}

/** The path alone: the query string and fragment can hold search words or ids, so they never go in a report. */
export function routeOf(path: string): string {
  return cut(path.split(/[?#]/, 1)[0], ROUTE_MAX);
}

/**
 * A message as the user saw it: its title, then its description when that is text. Apps often
 * put the reason in the description ('Could not save', { description: e.message }).
 */
export function shownErrorText(title: string, description?: unknown): string {
  const detail = typeof description === 'string' ? description.trim() : '';
  return detail ? `${title.trim()} — ${detail}` : title;
}

/** Note a message the user was shown. Blank messages are ignored. */
export function noteShownError(message: string, now = new Date()): void {
  const trimmed = message.trim();
  if (!trimmed) return;
  shown = keepLast(shown, { message: cut(trimmed, RECENT_ERROR_MESSAGE_MAX), at: now.toISOString() });
}

/** Note a failed call to the app's API. */
export function noteFailedCall(call: { path: string; method?: string; status: number; code?: string; message: string }, now = new Date()): void {
  failedCalls = keepLast(failedCalls, {
    route: routeOf(call.path),
    method: (call.method ?? 'GET').toUpperCase(),
    status: call.status,
    ...(call.code ? { code: cut(call.code, CODE_MAX) } : {}),
    message: cut(call.message.trim(), RECENT_ERROR_MESSAGE_MAX),
    at: now.toISOString(),
  });
}

/** A copy of what's kept, oldest first. */
export function recentErrors(): RecentErrors {
  return { shown: [...shown], failedCalls: [...failedCalls] };
}

/** Forget everything (a test seam). */
export function clearRecentErrors(): void {
  shown = [];
  failedCalls = [];
}

// ---------- the API client ----------

// Fields written out, not as constructor parameter properties, so node's
// type-stripping test runner can load this file.
export class ApiError extends Error {
  status: number;
  /** The API's machine-readable reason, e.g. STALE or OVERLAP. */
  code?: string;
  /** The whole error body, for routes that send more than a message (or the raw reply, when it wasn't JSON). */
  body?: unknown;

  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

const HTTP_NO_CONTENT = 204;
const HTTP_GATEWAY_TIMEOUT = 504;
const REPLY_LOST =
  'The request took too long to answer — the change may still have been applied. Check before retrying.';

/** Longer than this, a non-JSON reply is a page, not a message. */
const MAX_PLAIN_MESSAGE = 200;
const HTTP_SERVER_ERROR = 500;

/**
 * A reply that isn't our API's JSON: a short plain-text line stays the
 * message; a page (a proxy's or gateway's HTML 502, #387) never reaches the
 * screen — it becomes a short sentence, and the page is kept in `body` for logs.
 */
function messageFromText(status: number, statusText: string, text: string): string {
  const trimmed = text.trim();
  if (trimmed && trimmed.length <= MAX_PLAIN_MESSAGE && !trimmed.includes('<')) return trimmed;
  if (status >= HTTP_SERVER_ERROR) return `The server couldn't be reached just now (HTTP ${status}). Try again in a moment.`;
  return statusText && !trimmed ? statusText : `The server sent an unexpected reply (HTTP ${status}).`;
}

async function toApiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { error?: string; code?: string };
    if (body.error) return new ApiError(res.status, body.error, body.code, body);
  } catch {
    // not JSON — fall through to the reply's text
  }
  return new ApiError(res.status, messageFromText(res.status, res.statusText, text), undefined, text || undefined);
}

export function createApiClient(options: {
  /** The signed-in user's token for this app's API (createMsalApp's getAccessToken). */
  getToken: () => Promise<string>;
  /** Prefix for every path; default '/api'. */
  base?: string;
}) {
  const base = options.base ?? '/api';

  /** The raw response (for file downloads); throws ApiError on a failure status. */
  async function request(path: string, init?: RequestInit): Promise<Response> {
    const bearer = `Bearer ${await options.getToken()}`;
    // new Headers() accepts every HeadersInit shape (a spread would drop a
    // Headers object or [key, value] pairs).
    const headers = new Headers(init?.headers);
    // JSON by default; a FormData upload needs the browser's multipart boundary.
    if (!headers.has('Content-Type') && !(init?.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    // Set last, so no caller can send another token in place of the user's.
    headers.set('X-App-Authorization', bearer);
    headers.set('Authorization', bearer);
    const method = init?.method ?? 'GET';
    let res: Response;
    try {
      res = await fetch(`${base}${path}`, { ...init, headers });
    } catch (err) {
      // No answer at all (no signal, blocked): noted for a feedback report, then passed on as it was.
      noteFailedCall({ path: `${base}${path}`, method, status: 0, message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
    if (!res.ok) {
      const error = await toApiError(res);
      noteFailedCall({ path: `${base}${path}`, method, status: error.status, code: error.code, message: error.message });
      throw error;
    }
    return res;
  }

  /** The parsed JSON reply; undefined for a 204. */
  async function json<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await request(path, init);
    if (res.status === HTTP_NO_CONTENT) return undefined as T;
    try {
      // Inside the try: a reply cut off mid-body can fail the read itself.
      const text = await res.text();
      if (!text) throw new Error('empty');
      return JSON.parse(text) as T;
    } catch {
      noteFailedCall({ path: `${base}${path}`, method: init?.method, status: HTTP_GATEWAY_TIMEOUT, message: REPLY_LOST });
      throw new ApiError(HTTP_GATEWAY_TIMEOUT, REPLY_LOST);
    }
  }

  return { request, json };
}

/**
 * The Report a problem / Suggest button's send (#511): the report to the app's own
 * `POST /api/feedback`, through the app's client, answered with its reference. `path` is
 * relative to the client's base, so an app whose paths include `/api` passes '/api/feedback'.
 * Call it once, at module level: the button keeps one queue per send function.
 */
export function feedbackSender(json: <T>(path: string, init?: RequestInit) => Promise<T>, path = '/feedback') {
  return (report: FeedbackReport) => json<{ reference: string }>(path, { method: 'POST', body: JSON.stringify(report) });
}
